/**
 * ArenaMultiLevel — arena-multi-level.
 *
 * The MULTI-LEVEL PvP testing arena (DJ directive 2026-06-04). What /brawl was
 * originally intended to be: stacked, reachable tiers so PvP can be tested at
 * every elevation + range. /brawl stays the flat chaotic free-for-all; this is
 * the deliberate, structured one.
 *
 * Layout — symmetric stepped "colosseum", 120×52 tiles (3840×1664 px). A
 * staircase climbs both sides toward a central high-ground; each step is ~6
 * tiles (192 px) — a single jump — and the steps overlap horizontally so every
 * tier is reachable. Gaps between the wings let you fall back down.
 *
 *     x=0 ─────────────────────────────── x=119
 *  y=0  ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓  ← ceiling
 *       ▓                                ▓
 *  y=22 ▓            �8�8�8�8�8             ▓  ← high-ground (top, x50..70)
 *  y=28 ▓        ▆▆▆▆▆▆▆▆▆▆▆▆▆▆          ▓  ← tier-3 (x42..78)
 *  y=34 ▓    ▅▅▅▅▅▅            ▅▅▅▅▅▅    ▓  ← tier-2 wings (x26..48 / x72..94)
 *  y=40 ▓ ▄▄▄▄▄▄                ▄▄▄▄▄▄  ▓  ← tier-1 wings (x8..30 / x90..112)
 *       ▓     │                 │       ▓  ← cover pillars (x36, x82)
 *  y=46 ▓════════════════════════════════▓  ← floor
 *  y=51 ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
 *
 * Respawn: type='field' + multiple spawnPoints + no defaultSpawn → MapSystem
 * random-picks one (spread across the tiers). Distinct competitive-blue palette
 * (vs brawl's crimson). PvP + opponent roster baked from ARENA_CONFIG.
 */

import { PlatformerLevel } from '../../shared/CharacterTypes';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';
import { ARENA_CONFIG } from '../../config/arena/ArenaConfig';

const MAP_ID = 'arena-multi-level';
const MAP_NAME = 'Arena';
const VERSION = '1.0';

const T = 32;   // tile size (px)
const W = 120;  // tiles wide → 3840 px
const H = 52;   // tiles tall → 1664 px
const WALL = 4; // wall thickness (tiles)
const FLOOR_TOP = 46; // top row of the floor

// Spawn points spread across the tiers (y ≈ 2 tiles above each platform top so
// the player drops onto it). No defaultSpawn → random re-roll each respawn.
const SPAWN_TILES = [
  { name: 'ground-L', x: 14,  y: 44 },
  { name: 'ground-R', x: 106, y: 44 },
  { name: 'mid-L',    x: 18,  y: 38 },
  { name: 'mid-R',    x: 102, y: 38 },
  { name: 'top',      x: 60,  y: 20 },
];
const spawnPoints = SPAWN_TILES.map((s) => ({ x: s.x * T, y: s.y * T }));

// ============================================================================
// RUNTIME PLATFORMS — floor + walls + stepped tiers + cover
// ============================================================================
const tier = (id: string, tx: number, ty: number, tw: number, color = '#22304f') => ({
  id, x: tx * T, y: ty * T, width: tw * T, height: 2 * T, type: 'normal' as const, color,
});

const runtimePlatforms = [
  // Floor (full width)
  { id: 'floor', x: 0, y: FLOOR_TOP * T, width: W * T, height: (H - FLOOR_TOP) * T, type: 'normal' as const, color: '#162036' },
  // Ceiling
  { id: 'ceiling', x: 0, y: 0, width: W * T, height: 3 * T, type: 'normal' as const, color: '#0e1626' },
  // Walls
  { id: 'wall-left',  x: 0, y: 0, width: WALL * T, height: H * T, type: 'normal' as const, color: '#0e1626' },
  { id: 'wall-right', x: (W - WALL) * T, y: 0, width: WALL * T, height: H * T, type: 'normal' as const, color: '#0e1626' },

  // Stepped tiers — symmetric staircase climbing to the central high-ground.
  // Each ~6 tiles above the previous (single jump); overlap horizontally so the
  // next step is reachable. Left + mirrored right.
  tier('tier1-left',  8,  40, 22),  // x 8..30
  tier('tier1-right', 90, 40, 22),  // x 90..112
  tier('tier2-left',  26, 34, 22),  // x 26..48 (overlaps tier1-left)
  tier('tier2-right', 72, 34, 22),  // x 72..94
  tier('tier3-left',  42, 28, 18),  // x 42..60
  tier('tier3-right', 60, 28, 18),  // x 60..78 (tier3 L+R form a wide center landing)
  // Central high-ground (the prize tier)
  { id: 'high-ground', x: 50 * T, y: 22 * T, width: 20 * T, height: 3 * T, type: 'normal' as const, color: '#2c3d63' },

  // Cover pillars on the floor — block projectiles + LOS, walk-through.
  { id: 'cover-left',  x: 36 * T, y: 40 * T, width: 2 * T, height: 6 * T, type: 'cover' as const, color: '#3a4d73' },
  { id: 'cover-right', x: 82 * T, y: 40 * T, width: 2 * T, height: 6 * T, type: 'cover' as const, color: '#3a4d73' },
];

// ============================================================================
// PARALLAX — cool competitive blue, distinct from brawl's crimson
// ============================================================================

// ============================================================================
// EXPORT
// ============================================================================
export const ArenaMultiLevel: GridLevel & Partial<PlatformerLevel> = {
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,

  widthTiles: W,
  heightTiles: H,
  width: W * T,
  height: H * T,
  tileSize: T,

  backgroundColor: '#070b14', // very dark blue — reads "competitive arena"
  gravity: 1000,

  spawnPoints,

  layers: [
    { name: LayerType.GROUND,    visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.OBJECTS,   visible: true, locked: false, opacity: 1.0, tiles: new Map() },
    { name: LayerType.ENTITIES,  visible: true, locked: false, opacity: 1.0, tiles: new Map(), entities: [] },
    { name: LayerType.TRIGGERS,  visible: true, locked: false, opacity: 1.0, tiles: new Map() },
  ],

  platforms: runtimePlatforms,
  entities: [],
  spawnRules: [],
  type: 'field', // random spawn re-roll each respawn

  metadata: {
    author: 'DJ',
    description: 'Multi-level PvP testing arena — 120×52, stacked tiers (ground / mid ledges / high-ground), cover pillars. Opponents spread across the levels. PvP defaults from ARENA_CONFIG.',
    tags: ['pvp', 'arena', 'multi-level', 'testing'],
    lobbyTools: { enabled: true },
    spawnSnapMaxFallPx: 200,
    profile: 'pvp_arena',
    overrides: {
      pvp: {
        enabled: ARENA_CONFIG.pvpEnabled,
        friendlyFire: ARENA_CONFIG.friendlyFire,
        respawnDelayMs: ARENA_CONFIG.respawnDelayMs,
        respawnInvulnerabilityMs: ARENA_CONFIG.respawnInvulnerabilityMs,
        botBehavior: ARENA_CONFIG.botBehavior,
      },
      bot: {
        lobbyCount: ARENA_CONFIG.botRoster.length,
        lobbyClasses: ARENA_CONFIG.botRoster.map((b) => b.class),
        spawnRingRadius: 0,
        colorScheme: 'distinct',
        behaviorTier: ARENA_CONFIG.botRoster[0]?.tier ?? 'aggressive',
        lobbyTiers: ARENA_CONFIG.botRoster.map((b) => b.tier),
        lobbyNames: ARENA_CONFIG.botRoster.map((b) => b.name),
        lobbyPositions: ARENA_CONFIG.botRoster.map((b) => ({ x: b.tileX * T, y: b.tileY * T })),
      },
    },
  },
};

export { W, H, T, MAP_ID, MAP_NAME, SPAWN_TILES };
